import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { useAppDispatch } from 'stores/hooks';
import { useTreatments } from 'utils/treatments';
import { fetchSpotMapData } from 'actions/spotMapData';
import { useIsInternalUser } from 'selectors/user';
import type { Map } from 'leaflet';

import type { BoundingBox } from 'types/map';

interface UseSpotDataFetchingProps {
  leafletMapRef: React.MutableRefObject<Map | null>;
  clusterMaxZoom: number;
  onFetchStart?: () => void;
  onFetchComplete?: () => void;
}

/**
 * Hook to handle fetching spot data with debouncing
 */
export const useSpotDataFetching = ({
  leafletMapRef,
  clusterMaxZoom,
  onFetchStart,
  onFetchComplete,
}: UseSpotDataFetchingProps) => {
  const dispatch = useAppDispatch();
  const treatments = useTreatments();
  const isInternalUser = useIsInternalUser();
  const spotsUpdateScheduledRef = useRef<boolean>(false);
  const debouncedFuncRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Fetch spot data with bounds checking
  const fetchSpotData = useCallback(
    (bounds: BoundingBox, doCluster: boolean) => {
      // Call the onFetchStart callback if provided
      if (onFetchStart) {
        onFetchStart();
      }

      dispatch(fetchSpotMapData(bounds, doCluster, isInternalUser))
        .then(() => {
          // Call the onFetchComplete callback if provided
          if (onFetchComplete) {
            onFetchComplete();
          }
        })
        .catch(() => {
          // Make sure to call onFetchComplete even if there's an error
          if (onFetchComplete) {
            onFetchComplete();
          }
        });
    },
    [dispatch, onFetchStart, onFetchComplete, isInternalUser],
  );

  // Create the update function and store it in a ref
  const updateSpotsFunctionRef = useRef<() => void>(() => {});

  // Initialize the update function
  useEffect(() => {
    // Define the function that does the actual work
    updateSpotsFunctionRef.current = () => {
      if (!leafletMapRef.current) return;

      try {
        const bounds = leafletMapRef.current.getBounds();
        if (bounds) {
          spotsUpdateScheduledRef.current = false;
          fetchSpotData(
            {
              north: bounds.getNorth(),
              south: bounds.getSouth(),
              west: bounds.getWest(),
              east: bounds.getEast(),
            },
            leafletMapRef.current.getZoom() <= clusterMaxZoom,
          );
        }
      } catch (_error) {
        spotsUpdateScheduledRef.current = false;
      }
    };

    // Create debounced version and store in ref
    // Using a longer wait time (2000ms) to replace the manual throttle
    debouncedFuncRef.current = debounce(updateSpotsFunctionRef.current, 250, {
      maxWait: 2000,
      leading: false,
      trailing: true,
    });

    return () => {
      if (debouncedFuncRef.current) {
        debouncedFuncRef.current.cancel();
      }
    };
  }, [fetchSpotData, leafletMapRef, clusterMaxZoom, treatments]);

  // Stable function to call the debounced function through the ref
  const updateSpots = useCallback(() => {
    if (debouncedFuncRef.current) {
      debouncedFuncRef.current();
    }
  }, []);

  return {
    updateSpots,
    spotsUpdateScheduledRef,
  };
};

export default useSpotDataFetching;
